import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, HeartHandshake, Stethoscope, Baby } from "lucide-react";

const serviceLinks = [
  { label: "Patients & Elderly Care", href: "/services/patients-elderly-care", icon: HeartHandshake },
  { label: "Home Nursing", href: "/services/home-nursing", icon: Stethoscope },
  { label: "Nanny / Baby Sitting", href: "/services/nanny-baby-sitting", icon: Baby },
];

const navLinks = [
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Why Us", href: "/#why-us" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "FAQ", href: "/#faq" },
  { label: "Join Us", href: "/#join-us" },
  { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
        : "bg-white/80 backdrop-blur-sm"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="navbar-logo" className="flex flex-col items-start group">
            <img
              src="/images/logo-full.svg"
              alt="CareCrony Logo"
              className="h-8 md:h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-[8px] md:text-[10px] font-bold text-[#dd6b8e] tracking-[0.15em] uppercase -mt-1 ml-10 font-body">
              Care Giving Services
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Services Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                data-testid="nav-services-dropdown"
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-sm font-medium text-[#475569] hover:text-[#14B8A6] transition-colors"
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      data-testid={`nav-service-${s.href.split("/").pop()}`}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-[#475569] hover:text-[#14B8A6] hover:bg-[#F0FDFA] transition-colors"
                      onClick={() => setServicesOpen(false)}
                    >
                      <s.icon className="w-4 h-4" strokeWidth={1.5} />
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-sm font-medium text-[#475569] hover:text-[#14B8A6] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <a
              href="/#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick("/#contact"); }}
              data-testid="navbar-request-callback"
              className="inline-flex items-center gap-2 h-10 px-6 rounded-full text-sm font-semibold bg-[#14B8A6] text-white hover:bg-[#0D9488] transition-all shadow-md shadow-teal-500/20"
            >
              Request Call Back
            </a>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            className="lg:hidden p-2 text-[#0F172A]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="flex items-center justify-between w-full py-2 text-sm font-medium text-[#475569]"
            >
              Services
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 space-y-1">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.href}
                    to={s.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 py-2 text-sm text-[#475569] hover:text-[#14B8A6]"
                  >
                    <s.icon className="w-4 h-4" strokeWidth={1.5} />
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="block py-2 text-sm font-medium text-[#475569] hover:text-[#14B8A6]"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick("/#contact"); }}
              className="block mt-3 text-center py-3 rounded-full text-sm font-semibold bg-[#14B8A6] text-white"
            >
              Request Call Back
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
